import React, { useId } from 'react';
import { cn } from '../../utils/cn';

export interface SegmentOption<T extends string> {
  value: T;
  label: string;
  /** Optional small tag (e.g. a `Badge`) pinned to the option's top edge, off the label line. */
  badge?: React.ReactNode;
  /** Overrides the button's accessible name (e.g. to spell out a `badge`'s meaning: "12 Monate, bester Wert"). */
  ariaLabel?: string;
}

export interface SegmentedControlProps<T extends string> {
  value: T;
  onChange: (value: T) => void;
  options: SegmentOption<T>[];
  /** Accessible group name; visually hidden unless showLabel. */
  label: string;
  name?: string;
  size?: 'sm' | 'md';
  fullWidth?: boolean;
  disabled?: boolean;
  showLabel?: boolean;
  className?: string;
}

export function SegmentedControl<T extends string>({
  value,
  onChange,
  options,
  label,
  name,
  size = 'md',
  fullWidth,
  disabled,
  showLabel,
  className,
}: SegmentedControlProps<T>) {
  const groupId = useId();

  const focusOption = (idx: number) => {
    (document.getElementById(`${groupId}-${idx}`) as HTMLButtonElement | null)?.focus();
  };

  const handleKeyDown = (event: React.KeyboardEvent, idx: number) => {
    if (disabled) return;
    let nextIdx: number | null = null;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') nextIdx = (idx + 1) % options.length;
    else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') nextIdx = (idx - 1 + options.length) % options.length;
    else if (event.key === 'Home') nextIdx = 0;
    else if (event.key === 'End') nextIdx = options.length - 1;
    if (nextIdx === null) return;
    event.preventDefault();
    onChange(options[nextIdx].value);
    focusOption(nextIdx);
  };

  return (
    <div className={className} data-name={name}>
      <span id={`${groupId}-label`} className={showLabel ? 'mb-1.5 block text-sm font-medium text-ink' : 'sr-only'}>
        {label}
      </span>
      <div
        role="radiogroup"
        aria-labelledby={`${groupId}-label`}
        className={cn(
          'inline-flex flex-wrap gap-1 rounded-control bg-sunken p-1',
          // Room above (and between wrapped rows) for badges that sit on the top edge.
          options.some((o) => o.badge) && 'mt-2.5 gap-y-3.5',
          fullWidth && 'flex w-full'
        )}
      >
        {options.map((option, idx) => {
          // Tighter padding keeps a badged row (the 4 Laufzeiten) on one line from 360px.
          const anyBadge = options.some((o) => o.badge);
          const selected = option.value === value;
          return (
            <button
              key={option.value}
              type="button"
              id={`${groupId}-${idx}`}
              role="radio"
              aria-checked={selected}
              aria-label={option.ariaLabel}
              tabIndex={selected ? 0 : -1}
              disabled={disabled}
              onClick={() => onChange(option.value)}
              onKeyDown={(event) => handleKeyDown(event, idx)}
              className={cn(
                'relative flex items-center justify-center rounded-control text-sm font-medium transition-colors',
                anyBadge ? 'px-2 sm:px-3' : 'px-3',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand',
                size === 'sm' ? 'h-8' : 'h-9',
                fullWidth && 'flex-1',
                selected ? 'bg-brand text-on-brand' : 'text-ink hover:bg-card',
                disabled && 'cursor-not-allowed opacity-50'
              )}
            >
              {/* Badge (e.g. "Bester Wert") straddles the top edge instead of sitting inline or
                  below: every label stays on one line at one height, and options stay narrow
                  enough for 4 to wrap into 2×2 instead of overflowing at 360px (M9). */}
              <span>{option.label}</span>
              {option.badge && (
                <span className="pointer-events-none absolute -top-2.5 left-1/2 -translate-x-1/2 whitespace-nowrap leading-none">
                  {option.badge}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
